import axiosInstance from "../../../api/axiosConfig";
import { handleAPIError } from "../../../api/utils/errors";
import type { OutgoingIntakeFile } from "../../../api/types/outgoingIntake";
import type {
  CaseyAttachment,
  CaseyConversation,
  SettledProposalDecision
} from "../types";

/**
 * The chat route is reached by `fetch` rather than this axios instance, because
 * the AI SDK transport needs the raw streaming `Response` body. Its URL is built
 * here so both halves agree on the base.
 */
const rawBaseURL = import.meta.env.REACT_APP_API_BASE_URL;
const baseURL =
  rawBaseURL && typeof rawBaseURL === "string"
    ? rawBaseURL.replace(/\/+$/, "")
    : "";

export function caseyChatUrl(token: string): string {
  return `${baseURL}/public/outgoing_intakes/${encodeURIComponent(
    token
  )}/assistant/chat`;
}

export const IntakeAssistantAPI = {
  /**
   * The conversation so far, so reopening the intake shows it rather than an
   * empty panel. Carries the attachments and the settled suggestions with it,
   * neither of which can be rebuilt from the transcript alone.
   */
  async fetchConversation(token: string): Promise<CaseyConversation> {
    try {
      const response = await axiosInstance.get<CaseyConversation>(
        `/public/outgoing_intakes/${token}/assistant/conversation`
      );
      return response.data;
    } catch (error) {
      const apiError = handleAPIError(error);
      throw apiError;
    }
  },

  /**
   * Remembers what the client did with one or more suggestions.
   *
   * A map so accepting a whole set is one request. Nothing about the answer
   * itself travels here; that is written by the form's own save.
   */
  async saveProposalDecisions(
    token: string,
    decisions: Record<string, SettledProposalDecision>
  ): Promise<void> {
    try {
      await axiosInstance.post(
        `/public/outgoing_intakes/${token}/assistant/proposal_decisions`,
        { decisions }
      );
    } catch (error) {
      const apiError = handleAPIError(error);
      throw apiError;
    }
  },

  /**
   * Hands a document to Casey. Extraction runs server-side afterwards, so the
   * returned attachment is normally still `pending`; poll `getAttachment` for
   * the outcome.
   */
  async uploadAttachment(token: string, file: File): Promise<CaseyAttachment> {
    try {
      const formData = new FormData();
      formData.append("file", file);
      const response = await axiosInstance.post<{
        attachment: CaseyAttachment;
      }>(`/public/outgoing_intakes/${token}/assistant/attachments`, formData, {
        headers: { "Content-Type": "multipart/form-data" }
      });
      return response.data.attachment;
    } catch (error) {
      const apiError = handleAPIError(error);
      throw apiError;
    }
  },

  async getAttachment(
    token: string,
    attachmentId: string
  ): Promise<CaseyAttachment> {
    try {
      const response = await axiosInstance.get<CaseyAttachment>(
        `/public/outgoing_intakes/${token}/assistant/attachments/${attachmentId}`
      );
      return response.data;
    } catch (error) {
      const apiError = handleAPIError(error);
      throw apiError;
    }
  },

  /**
   * The rendition a suggestion's highlight is drawn on.
   *
   * Not the attachment's own bytes: the citation coordinates were measured
   * against the server's PDF conversion of it, and only that rendition puts the
   * highlight in the right place. 404s for anything extracted before the server
   * started keeping the copy, which the card handles by quoting the line instead.
   */
  async fetchCitationPdf(token: string, attachmentId: string): Promise<Blob> {
    try {
      const response = await axiosInstance.get<Blob>(
        `/public/outgoing_intakes/${token}/assistant/attachments/${attachmentId}/citation_pdf`,
        { responseType: "blob" }
      );
      return response.data;
    } catch (error) {
      const apiError = handleAPIError(error);
      throw apiError;
    }
  },

  /**
   * Promotes a chat attachment into a file on the intake.
   *
   * Called when the client accepts a document Casey offered. The server runs the
   * same ingest a manual upload gets, so the file that comes back is previewable
   * and review-checked; the attachment record on its own is neither.
   */
  async attachToIntake(
    token: string,
    attachmentId: string
  ): Promise<OutgoingIntakeFile> {
    try {
      const response = await axiosInstance.post<{ file: OutgoingIntakeFile }>(
        `/public/outgoing_intakes/${token}/assistant/attachments/${attachmentId}/intake_file`
      );
      return response.data.file;
    } catch (error) {
      const apiError = handleAPIError(error);
      throw apiError;
    }
  }
};
